import { ENV } from '@env';
import jwtDecode from 'jwt-decode';
import { random } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { tap, catchError, mapTo, switchMap } from 'rxjs/operators';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { AxonLogoutReason } from '@lib/axon';
import { FsDataDogRumService } from '@lib/data-dog-rum';

import { SnCurrentUser } from './current-user.service';

const TOKEN_STORAGE_KEY = 'JWT';
export const TOKEN_EXPIRE_KEY = 'jwtExpires';
const IS_REFRESHING_KEY = 'jwtRefreshing';
const ABLY_STORAGE_KEY = 'ablyToken';
const CJIS_APPROVED_KEY = 'cjisApproved';
const SSO_PROVIDER_STORAGE_KEY = 'ssoProvider';
const AZURE_WEBPUBSUB_URL = 'azureWebpubsubUrl';

export interface IAblyTokenResponse {
  token: string;
}

export type ObtainResponse = {
  token?: string;
  ablyToken?: string;
  azureWebpubsubUrl?: string;
  mfaEnabled?: boolean;
  twoFactorAuthRequired?: boolean;
};

export type SsoProvider = 'axon';

export type OutsideLogoutReason = `${string}:${string}`;
// Axon service logout reasons are prefixed with 'axon.service:'
export type PrefixedAxonLogoutReason = `axon.service:${AxonLogoutReason}`;
// See fieldwatch-api/src/auth/exceptions.py
export type LogoutReason =
  | 'refreshFailed'
  | 'sessionExpired'
  | 'tokenExpired'
  | 'tokenBlacklisted'
  | 'tokenInvalid'
  | 'userDeactivated'
  | 'userDoesNotExist'
  | 'userLogout'
  | 'verifyFailed'
  | OutsideLogoutReason;

@Injectable({ providedIn: 'root' })
export class SnSessionService {
  public webGlEnabled: boolean = true;
  private _sessionTimer: Subscription;
  private _email: string;
  private _isAxonSso: boolean | null = null;
  private _loggedIn$ = new BehaviorSubject<boolean>(false);
  private readonly _headers = new HttpHeaders().set('X-fs-no-auth', '');

  constructor(
    private readonly _router: Router,
    private readonly _httpClient: HttpClient,
    private readonly _currentUser: SnCurrentUser,
    private readonly _toastrService: ToastrService,
    private readonly _datadogService: FsDataDogRumService,
    @Optional() private readonly _ability?: SnAbilityService,
  ) {
    this._datadogService.setGlobalContextProperty('sso_provider', this.ssoProvider);
  }

  get tokenExpiring(): boolean {
    const expires = +localStorage.getItem(TOKEN_EXPIRE_KEY);
    if (!expires) {
      return false;
    }
    return expires - Date.now() / 1000 < 120;
  }

  get tokenRefreshing(): boolean {
    return localStorage.getItem(IS_REFRESHING_KEY) === '1';
  }

  get token(): string {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!localStorage.getItem(TOKEN_EXPIRE_KEY) && token) {
      this.setToken(token);
    }
    return token;
  }

  get loggedIn$(): Observable<boolean> {
    return this._loggedIn$.asObservable();
  }

  get ssoProvider(): SsoProvider | null {
    return localStorage.getItem(SSO_PROVIDER_STORAGE_KEY) as SsoProvider | null;
  }

  get isFederal(): boolean {
    return ENV.toString() === 'prod-us3';
  }

  get isAxonSso(): boolean {
    if (this._isAxonSso === null) {
      return this.ssoProvider === 'axon';
    }
    return this._isAxonSso;
  }

  public providers(prefix: string, test: boolean): Observable<any> {
    return this._httpClient.get(`/api/auth/saml/${prefix}/providers/`, {
      params: { isTest: test.toString() },
    });
  }

  public providersByEmail(email: string, app: string, test: boolean): Observable<any> {
    return this._httpClient.get(`/api/auth/saml/${email}/providers_by_email/`, {
      params: { app, isTest: test.toString() },
    });
  }

  public create(email: string, password: string): Observable<ObtainResponse> {
    return this._httpClient
      .post<ObtainResponse>(
        '/api/auth/jwt/obtain/',
        { email, password },
        { headers: this._headers },
      )
      .pipe(
        tap((response) => {
          this._logJwtObtain(response);
          if (!response.mfaEnabled && !response.twoFactorAuthRequired) {
            this._setToken(response.token, response.ablyToken, response.azureWebpubsubUrl);
          }
        }),
      );
  }

  public authTwoFactor(token: string, code: string): Observable<any> {
    return this._httpClient.post('/api/auth/jwt/two_factor/', { token, code }).pipe(
      tap((response: any) => this._currentUser.setUser(response.user)),
      tap((response) =>
        this._setToken(response.token, response.ablyToken, response.azureWebpubsubUrl),
      ),
      tap({
        error: () => {
          this._toastrService.error('Unable to authenticate OTP code. Please try again later.');
        },
      }),
    );
  }

  public authBackupCode(token: string, code: string): Observable<any> {
    return this._httpClient.post('/api/auth/jwt/backup_code/', { token, code }).pipe(
      tap((response: any) => this._currentUser.setUser(response.user)),
      tap((response) =>
        this._setToken(response.token, response.ablyToken, response.azureWebpubsubUrl),
      ),
      tap({
        error: () => {
          this._toastrService.error('Unable to authenticate Backup code. Please try again later.');
        },
      }),
    );
  }

  public getAblyToken(): Observable<IAblyTokenResponse> {
    return this._httpClient.get<IAblyTokenResponse>('/api/transport/ably/token/obtain/');
  }

  public setToken(token: string, ablyToken?: string, azureUrl?: string): void {
    this._setToken(token, ablyToken, azureUrl);
  }

  public setAblyToken(token: string): void {
    localStorage.setItem(ABLY_STORAGE_KEY, token);
  }

  public setAzureWebPubSubUrl(url: string): void {
    localStorage.setItem(AZURE_WEBPUBSUB_URL, url);
  }

  public setCjisApprovedKey(): void {
    localStorage.setItem(CJIS_APPROVED_KEY, '1');
  }

  public removeCjisApprovedKey(): void {
    localStorage.removeItem(CJIS_APPROVED_KEY);
  }

  public setSsoProvider(ssoProvider: SsoProvider): void {
    localStorage.setItem(SSO_PROVIDER_STORAGE_KEY, ssoProvider);
    this._datadogService.setGlobalContextProperty('sso_provider', ssoProvider);
  }

  public refresh(): Observable<string> {
    // the user has been taken to a pre-auth/logout screen we do not need to refresh their token
    if (this._router.routerState.snapshot.url.startsWith('/sso/error')) return;

    const currentToken = this.token;
    const tokenExpiryBeforeRefresh = localStorage.getItem(TOKEN_EXPIRE_KEY);
    const secondsUntilExpiry = this._getSecondsUntilExpiry(tokenExpiryBeforeRefresh);

    this._logRefreshAttempt(currentToken, tokenExpiryBeforeRefresh, secondsUntilExpiry);

    localStorage.setItem(IS_REFRESHING_KEY, '1');
    return this._httpClient
      .post('/api/auth/jwt/refresh/', { token: currentToken }, { headers: this._headers })
      .pipe(
        tap((response: any) => {
          this._logRefreshSuccess(currentToken, response);

          this._setToken(response.token, response.ablyToken, response.azureWebpubsubUrl);
        }),
        catchError((error) => {
          this._logRefreshFailed(currentToken, error, secondsUntilExpiry);
          const logoutReason = this._mapTokenErrorToLogoutReason(error);
          this.logoutWithoutBlacklist({
            reason: logoutReason,
          });

          this._router.navigate(['/auth', 'sign-in'], {
            queryParams: {
              email: this._email,
              redirectUrl: this._router.routerState.snapshot.url,
            },
          });

          return throwError(error);
        }),
      );
  }

  public check(): Observable<boolean> {
    const storageToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const ablyToken = localStorage.getItem(ABLY_STORAGE_KEY);
    const azureUrl = localStorage.getItem(AZURE_WEBPUBSUB_URL);
    const cjisApproved = localStorage.getItem(CJIS_APPROVED_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRE_KEY);
    const secondsUntilExpiry = this._getSecondsUntilExpiry(tokenExpiry);

    if (!cjisApproved) {
      return of(false);
    }

    if (storageToken && (ablyToken || azureUrl)) {
      this._logSessionCheck(storageToken, secondsUntilExpiry);

      this._setToken(this.token);
      return of(true);
    } else if (storageToken) {
      this._logVerifyAttempt(ablyToken, azureUrl);

      return this._httpClient
        .post('/api/auth/jwt/verify/', { token: storageToken }, { headers: this._headers })
        .pipe(
          tap((response: any) => {
            this._logVerifySuccess(storageToken, response);

            this._setToken(response.token, response.ablyToken, response.azureWebpubsubUrl);
          }),
          mapTo(true),
          catchError((error) => {
            this._logVerifyFailed(error);

            this.removeCjisApprovedKey();
            return of(false);
          }),
        );
    }
    if (ablyToken || azureUrl) {
      localStorage.setItem(ABLY_STORAGE_KEY, '');
      localStorage.setItem(AZURE_WEBPUBSUB_URL, '');
    }
    return of(false);
  }

  public logout({ reason }: { reason: LogoutReason }): void {
    this._logJwtLogout(reason);

    if (this.token) {
      this._httpClient.get('/api/auth/jwt/logout/').subscribe(() => {
        this.logoutWithoutBlacklist({
          reason,
        });
      });
    }
    setTimeout(() => {
      this.logoutWithoutBlacklist({
        reason,
      });
    }, 500);
  }

  /**
   * @param redirectToLogoutScreen shows an error screen if true and will not let the user attempt a re-login
   * @param reason must be one of the known reasons to the session service, or prefixed with the origin (e.g. 'axon.service:reason')
   */
  public logoutWithoutBlacklist({
    redirectToLogoutScreen,
    reason,
  }: {
    redirectToLogoutScreen?: boolean;
    reason: LogoutReason;
  }) {
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRE_KEY);
    const secondsUntilExpiry = this._getSecondsUntilExpiry(tokenExpiry);

    this._destroyToken();
    this.removeCjisApprovedKey();

    if (this._ability) {
      this._ability.destroyPermissions();
    }

    // Check if Axon Connect tokens are expired
    const axonToken = this._currentUser.settings?.authTokens?.axonconnect;
    let axonAccessTokenExpired: boolean | undefined;
    let axonRefreshTokenExpired: boolean | undefined;

    if (axonToken?.obtained) {
      const obtainedTimestamp = new Date(axonToken.obtained).getTime();
      if (!isNaN(obtainedTimestamp)) {
        const now = Date.now();

        // Check access token expiry (for API calls)
        if (axonToken.expiresIn) {
          const accessTokenExpiry = obtainedTimestamp + axonToken.expiresIn * 1000;
          axonAccessTokenExpired = now > accessTokenExpiry;
        }

        // Check refresh token expiry (for re-authentication requirement)
        if (axonToken.refreshTokenExpiresIn) {
          const refreshTokenExpiry = obtainedTimestamp + axonToken.refreshTokenExpiresIn * 1000;
          axonRefreshTokenExpired = now > refreshTokenExpiry;
        }
      }
    }

    const fususTokenExpired = this._isTokenExpired(secondsUntilExpiry);

    const isExpectedLogout = this._determineIfExpectedLogout({
      reason,
      fususTokenExpired,
      axonAccessTokenExpired,
      axonRefreshTokenExpired,
      isAxonSsoUser: this.ssoProvider === 'axon',
    });

    this._datadogService.addAction('logout_forced', {
      reason,
      redirectToLogoutScreen,
      fususTokenExpiry: tokenExpiry,
      fususTokenSecondsUntilExpiry: secondsUntilExpiry,
      fususTokenExpired,
      fususTokenExpiring: this.tokenExpiring,
      fususTokenRefreshing: this.tokenRefreshing,
      axonAccessTokenExpired,
      axonRefreshTokenExpired,
      hadTimer: !!this._sessionTimer,
      isExpectedLogout,
      ssoProvider: this.ssoProvider,
    });

    if (redirectToLogoutScreen) {
      this._router.navigate(['/sso', 'error'], { queryParams: { code: 4001 } });
      return;
    }

    this._router.navigate(['/auth', 'sign-in']);
  }

  public getAzureWebPubSubUrl(): Observable<string> {
    const azureUrl = localStorage.getItem(AZURE_WEBPUBSUB_URL);
    if (!azureUrl) {
      return throwError(new Error('Azure WebPubSub URL not found in session'));
    }
    return of(azureUrl);
  }

  public getAblyStorageKey(): Observable<string> {
    const ablyToken = localStorage.getItem(ABLY_STORAGE_KEY);
    if (!ablyToken) {
      return throwError(new Error('Ably token not found in session'));
    }
    return of(ablyToken);
  }

  private _setToken(token: string, ablyToken?: string, azureUrl?: string): void {
    const oldToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    const hadTimer = !!this._sessionTimer;

    if (this._sessionTimer) {
      this._sessionTimer.unsubscribe();
    }

    if (ablyToken) {
      localStorage.setItem(ABLY_STORAGE_KEY, ablyToken);
    }

    if (azureUrl) {
      localStorage.setItem(AZURE_WEBPUBSUB_URL, azureUrl);
    }

    const decodedToken = jwtDecode(token);
    this._email = decodedToken.email;
    this._loggedIn$.next(true);
    const tokenExpiresIn = decodedToken.exp * 1000 - Date.now();

    // log if axon sso was used
    this._isAxonSso = decodedToken.is_axon_sso ?? null;
    if (this._isAxonSso !== null) {
      this._datadogService.setGlobalContextProperty('is_axon_sso', this._isAxonSso);
    }

    // log expiry and obtained of fusus token in milliseconds
    this._datadogService.setGlobalContextProperty('fusus_token_expiry_ms', decodedToken.exp * 1000);
    this._datadogService.setGlobalContextProperty(
      'fusus_token_obtained_ms',
      decodedToken.iat * 1000,
    );
    // prevent conflict with other tabs with random number
    const randomSeconds = random(5, 120);
    const timerLength = tokenExpiresIn - randomSeconds * 1000;

    this._logTokenSet(oldToken, token, tokenExpiresIn, timerLength, hadTimer, ablyToken, azureUrl);

    this._sessionTimer = timer(timerLength)
      .pipe(
        switchMap(() => {
          if (this.tokenRefreshing) {
            setTimeout(() => {
              this._setToken(this.token);
            }, 1000);
            return of(null);
          }
          return this.refresh();
        }),
      )
      .subscribe();
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(TOKEN_EXPIRE_KEY, decodedToken.exp);
    localStorage.setItem(IS_REFRESHING_KEY, '0');
  }

  private _getTokenPreview(token: string | null | undefined): string | undefined {
    return token?.slice(-4);
  }

  private _getSecondsUntilExpiry(tokenExpiry: string | null): number | null {
    if (!tokenExpiry) return null;
    const expiry = +tokenExpiry;
    return !isNaN(expiry) ? Math.floor(expiry - Date.now() / 1000) : null;
  }

  private _isTokenExpired(secondsUntilExpiry: number | null): boolean {
    return secondsUntilExpiry !== null && secondsUntilExpiry < 0;
  }

  // Logging helpers
  private _logJwtObtain(response: any): void {
    this._datadogService.addAction('fusus_jwt_obtain', {
      tokenPreview: this._getTokenPreview(response.token),
    });
  }

  private _logRefreshAttempt(
    token: string,
    tokenExpiry: string | null,
    secondsUntilExpiry: number | null,
  ): void {
    this._datadogService.addAction('fusus_jwt_refresh_attempt', {
      tokenPreview: this._getTokenPreview(token),
      tokenExpiry,
      secondsUntilExpiry,
      hasTimer: !!this._sessionTimer,
    });
  }

  private _logRefreshSuccess(currentToken: string, response: any): void {
    this._datadogService.addAction('fusus_jwt_refresh_success', {
      oldTokenPreview: this._getTokenPreview(currentToken),
      newTokenPreview: this._getTokenPreview(response.token),
    });
  }

  private _logRefreshFailed(token: string, error: any, secondsUntilExpiry: number | null): void {
    const errorMessage = this._extractTokenErrorMessage(error);
    const statusCode = error?.status;

    this._datadogService.addAction('fusus_jwt_refresh_failed', {
      tokenPreview: this._getTokenPreview(token),
      statusCode,
      errorMessage,
      secondsUntilExpiry,
      errorResponse: error?.error,
    });
  }

  private _logSessionCheck(storageToken: string, secondsUntilExpiry: number | null): void {
    this._datadogService.addAction('fusus_session_check_calling_settoken', {
      tokenPreview: this._getTokenPreview(storageToken),
      hadTimer: !!this._sessionTimer,
      secondsUntilExpiry,
    });
  }

  private _logVerifyAttempt(ablyToken: string | null, azureUrl: string | null): void {
    this._datadogService.addAction('fusus_jwt_verify_attempt', {
      hasAblyToken: !!ablyToken,
      hasAzureUrl: !!azureUrl,
    });
  }

  private _logVerifySuccess(storageToken: string, response: any): void {
    this._datadogService.addAction('fusus_jwt_verify_success', {
      oldTokenPreview: this._getTokenPreview(storageToken),
      newTokenPreview: this._getTokenPreview(response.token),
    });
  }

  private _logVerifyFailed(error: any): void {
    const errorMessage = this._extractTokenErrorMessage(error);
    const statusCode = error?.status;

    this._datadogService.addAction('fusus_jwt_verify_failed', {
      statusCode,
      errorMessage,
      errorResponse: error?.error,
    });
  }

  private _logJwtLogout(reason: LogoutReason): void {
    this._datadogService.addAction('fusus_jwt_logout', {
      reason,
      tokenPreview: this._getTokenPreview(this.token),
    });
  }

  private _logTokenSet(
    oldToken: string | null,
    token: string,
    tokenExpiresIn: number,
    timerLength: number,
    hadTimer: boolean,
    ablyToken?: string,
    azureUrl?: string,
  ): void {
    this._datadogService.addAction('fusus_jwt_token_set', {
      oldTokenPreview: this._getTokenPreview(oldToken),
      newTokenPreview: this._getTokenPreview(token),
      secondsUntilExpiry: !isNaN(tokenExpiresIn) ? Math.floor(tokenExpiresIn / 1000) : null,
      secondsUntilAutoRefresh: !isNaN(timerLength) ? Math.floor(timerLength / 1000) : null,
      hadExistingTimer: hadTimer,
      hasAblyToken: !!ablyToken,
      hasAzureUrl: !!azureUrl,
    });
  }

  private _extractTokenErrorMessage(error: any): string | undefined {
    // Try standard error fields first
    if (error?.error?.detail) return error.error.detail;
    if (error?.error?.code) return error.error.code; // Fallback just in case it's available, but it's typically not

    // Handle DRF field-level validation errors (e.g., { token: ['This field may not be null.'] })
    if (error?.error && typeof error.error === 'object') {
      const firstField = Object.keys(error.error)[0];
      if (firstField && Array.isArray(error.error[firstField])) {
        const messages = error.error[firstField];
        return messages.length > 0 ? `${firstField}: ${messages[0]}` : undefined;
      }
    }

    return undefined;
  }

  private _mapTokenErrorToLogoutReason(error: any): LogoutReason {
    // Map backend error responses to specific logout reasons
    // Backend only sends 'detail' field in response (not 'code')
    // See fieldwatch-api/src/auth/exceptions.py
    const errorMessage = this._extractTokenErrorMessage(error);
    if (!errorMessage) {
      return 'refreshFailed';
    }

    const errorLower = errorMessage.toLowerCase();

    // Order matters - check most specific patterns first
    const errorMappings: Array<[string, LogoutReason]> = [
      ['session has expired', 'sessionExpired'],
      ['token has expired', 'tokenExpired'],
      ['blacklist', 'tokenBlacklisted'],
      ['invalid', 'tokenInvalid'],
      ['deactivated', 'userDeactivated'],
      ['does not exist', 'userDoesNotExist'],
    ];

    const match = errorMappings.find(([pattern]) => errorLower.includes(pattern));
    return match ? match[1] : 'refreshFailed';
  }

  /**
   * Determines if a logout is expected, unexpected, or ambiguous.
   * Returns:
   *   - true: 100% expected logout (user action, natural expiry, etc.)
   *   - false: 100% unexpected logout (API failure, token invalidation, etc.)
   *   - undefined: ambiguous case (insufficient context to determine)
   */
  private _determineIfExpectedLogout({
    reason,
    fususTokenExpired,
    axonAccessTokenExpired,
    axonRefreshTokenExpired,
    isAxonSsoUser,
  }: {
    reason: LogoutReason;
    fususTokenExpired: boolean;
    axonAccessTokenExpired: boolean | undefined;
    axonRefreshTokenExpired: boolean | undefined;
    isAxonSsoUser: boolean;
  }): boolean | undefined {
    const isOnSignInPage = this._router.url === '/' || this._router.url.includes('/auth/sign-in');
    if (isOnSignInPage) {
      return true;
    }

    const ALWAYS_EXPECTED_REASONS: Array<LogoutReason> = ['userLogout', 'sessionExpired'];
    if (ALWAYS_EXPECTED_REASONS.includes(reason)) {
      return true;
    }

    const ALWAYS_UNEXPECTED_REASONS: Array<LogoutReason> = [
      'tokenBlacklisted',
      'userDeactivated',
      'userDoesNotExist',
    ];
    if (ALWAYS_UNEXPECTED_REASONS.includes(reason)) {
      return false;
    }

    // Axon token specific reasons (all should be SSO users)
    const AXON_REASONS: Array<PrefixedAxonLogoutReason> = [
      'axon.service:appBarActionClicked',
      'axon.service:axonLayerEnabled',
      'axon.service:deviceSearchFailed',
      'axon.service:refreshFailedTooManyTimes',
      'axon.service:refreshFailedOnce',
    ];
    if (AXON_REASONS.includes(reason as PrefixedAxonLogoutReason)) {
      // Axon token errors shouldn't log out non-SSO users
      if (!isAxonSsoUser) {
        return false;
      }

      // Refresh failures need token context
      if (
        reason === 'axon.service:refreshFailedTooManyTimes' ||
        reason === 'axon.service:refreshFailedOnce'
      ) {
        // Ambiguous without Axon token context (check for both null and undefined)
        if (axonRefreshTokenExpired == null && axonAccessTokenExpired == null) {
          return undefined;
        }

        return axonRefreshTokenExpired === true;
      }

      // All other Axon reasons are currently unexpected:
      // - appBarActionClicked/axonLayerEnabled: user actions that triggered failed token checks
      // - deviceSearchFailed: device search operation failed
      return false;
    }

    if (reason === 'tokenExpired') {
      return fususTokenExpired;
    }

    if (reason === 'tokenInvalid') {
      // Ambiguous if token expired (could be natural expiry or corruption)
      if (fususTokenExpired) return undefined;
      // Unexpected if token still valid (tampered/malformed)
      return false;
    }

    // Fallback errors (generic refresh/verify failures)
    const FALLBACK_ERROR_REASONS: Array<LogoutReason> = ['refreshFailed', 'verifyFailed'];
    if (FALLBACK_ERROR_REASONS.includes(reason)) {
      return false;
    }

    return undefined;
  }

  private _destroyToken(): void {
    this._email = null;
    this._isAxonSso = null;
    this._loggedIn$.next(false);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(ABLY_STORAGE_KEY);
    localStorage.removeItem(AZURE_WEBPUBSUB_URL);
    localStorage.removeItem(TOKEN_EXPIRE_KEY);
    localStorage.removeItem('callSignChecked');
    localStorage.removeItem(SSO_PROVIDER_STORAGE_KEY);
    this._currentUser.clear();
  }
}
